import { CanActivate } from "@angular/router";
import { inject, Injectable } from "@angular/core";
import { defer, from, Observable, of, race, switchMap } from "rxjs";
import { delay } from "rxjs/operators";
import { sntInfo } from "src/app/common/dialog";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { ProductService } from "src/app/services/product.service";
import { sntLog } from "src/app/common/logger";
import { SoftwareProduct } from "src/app/support/product";

type SnootsBroadcastMessage = {
  type: "ping" | "pong";
};

const logger = sntLog("AllowExecutionSingleTabGuard");

@Injectable()
export class AllowExecutionSingleTabGuard implements CanActivate {
  private readonly productService = inject(ProductService);

  private executed: boolean = false;

  canActivate(): Observable<boolean> {
    logger.debug("canActivate");

    if (this.executed) {
      return of(true);
    }

    this.executed = true;

    const observableBroadcatsChannel = new Observable<boolean>((subscriber) => {
      const broadcastChannel = new BroadcastChannel("snoots_channel");

      broadcastChannel.onmessage = (
        message: MessageEvent<SnootsBroadcastMessage>
      ) => {
        switch (message.data.type) {
          case "ping": {
            logger.debug("onPing");

            logger.debug("sending pong");

            broadcastChannel.postMessage(<SnootsBroadcastMessage>{
              type: "pong"
            });
            break;
          }
          case "pong": {
            logger.debug("onPong");

            subscriber.next(false);
            subscriber.complete();
            break;
          }
        }
      };

      logger.debug("sending ping");
      broadcastChannel.postMessage(<SnootsBroadcastMessage>{
        type: "ping"
      });
    });

    return race(observableBroadcatsChannel, of(true).pipe(delay(250))).pipe(
      switchMap((canContinue: boolean) => {
        if (!canContinue) {
          return defer(() => {
            return from(
              // il promise non viene risolto intenzionalmente in modo da non completare
              // la navigazione angular e preservare l'url nella barra degli indirizzi
              // quando l'utente cliccherà su "Procedi" viene ricaricata la pagina sullo stesso url
              new Promise<boolean>(() => {
                let message: string;

                switch (this.productService.getCurrentProduct()) {
                  case SoftwareProduct.SNOOTS: {
                    message = sntTranslate(
                      marker(
                        'Questo software essere eseguito su di un solo tab per volta. Assicurati di aver chiuso gli altri tab del browser e poi clicca su "Procedi"'
                      )
                    );
                    break;
                  }
                  case SoftwareProduct.ROCKET: {
                    message = sntTranslate(
                      marker(
                        'Rocket può essere eseguito su di un solo tab per volta. Assicurati di aver chiuso gli altri tab del browser e poi clicca su "Procedi"'
                      )
                    );
                    break;
                  }
                  case SoftwareProduct.PMS: {
                    message = sntTranslate(
                      marker(
                        'Il PMS può essere eseguito su di un solo tab per volta. Assicurati di aver chiuso gli altri tab del browser e poi clicca su "Procedi"'
                      )
                    );
                    break;
                  }
                  case SoftwareProduct.ADMIN: {
                    message = sntTranslate(
                      marker(
                        'Admin può essere eseguito su di un solo tab per volta. Assicurati di aver chiuso gli altri tab del browser e poi clicca su "Procedi"'
                      )
                    );
                    break;
                  }
                  case SoftwareProduct.DROP_PAGE: {
                    message = sntTranslate(
                      marker(
                        'DropPage può essere eseguito su di un solo tab per volta. Assicurati di aver chiuso gli altri tab del browser e poi clicca su "Procedi"'
                      )
                    );
                    break;
                  }
                  case SoftwareProduct.PLATFORM: {
                    message = sntTranslate(
                      marker(
                        'Platform puù essere eseguito su di un solo tab per volta. Assicurati di aver chiuso gli altri tab del browser e poi clicca su "Procedi"'
                      )
                    );
                    break;
                  }
                  case SoftwareProduct.BOTMASTER: {
                    message = sntTranslate(
                      marker(
                        'Botmaster può essere eseguito su di un solo tab per volta. Assicurati di aver chiuso gli altri tab del browser e poi clicca su "Procedi"'
                      )
                    );
                    break;
                  }
                }

                sntInfo(sntTranslate(marker("Attenzione")), message, {
                  confirmText: sntTranslate(marker("Procedi"))
                }).then(() => {
                  window.location.reload();
                });
              })
            );
          });
        }

        return of(true);
      })
    );
  }
}
